import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  Outlet,
} from "@tanstack/react-router";
import type { JSX } from "react";

import { Agent } from "./components/Agent";
import { Alerts } from "./components/Alerts";
import { AlertRuleCreate } from "./components/AlertRuleCreate";
import { AlertRuleSilence } from "./components/AlertRuleSilence";
import { ReviewList } from "./components/ReviewList";
import { ReviewMarker } from "./components/review/ReviewMarker";
import { parseReviewMarkerSearch } from "./components/review/reviewMarkerData";
import { parseReviewListSearch } from "./components/review/reviewListData";
import { ApiKeysSettings } from "./components/ApiKeysSettings";
import { AppLayout } from "./components/AppLayout";
import { AuditLogSettings } from "./components/AuditLogSettings";
import { BillingSettings } from "./components/BillingSettings";
import { PhoneNumbersSettings } from "./components/PhoneNumbersSettings";
import { BillingCheckoutGate } from "./components/BillingCheckoutGate";
import { CheckoutLayout } from "./components/CheckoutLayout";
import { CheckoutReturn } from "./components/CheckoutReturn";
import { CheckoutScreen } from "./components/CheckoutScreen";
import { Dashboard } from "./components/Dashboard";
import { InvitationsSettings } from "./components/InvitationsSettings";
import { LicenseSettings } from "./components/LicenseSettings";
import { MarkerCreate } from "./components/MarkerCreate";
import { MarkerEdit } from "./components/MarkerEdit";
import { Markers } from "./components/Markers";
import { MembershipsSettings } from "./components/MembershipsSettings";
import { OrganizationSettings } from "./components/OrganizationSettings";
import { Personas } from "./components/Personas";
import { Scenarios } from "./components/Scenarios";
import { ScenarioRows } from "./components/ScenarioRows";
import { SimulationCreate } from "./components/SimulationCreate";
import { Simulations } from "./components/Simulations";
import { TemplateCreate } from "./components/TemplateCreate";
import { TemplateEdit } from "./components/TemplateEdit";
import { ProfileSettings } from "./components/ProfileSettings";
import { RetentionSettings } from "./components/RetentionSettings";
import { TeamDetailSettings } from "./components/TeamDetailSettings";
import { TraceDetail } from "./components/TraceDetail";
import { TeamsSettings } from "./components/TeamsSettings";
import { Transcripts } from "./components/Transcripts";
import { UsageSettings } from "./components/UsageSettings";
import { Variables } from "./components/Variables";
import { TranscriptTimeline } from "./components/TranscriptTimeline";
import { AgentsIndex } from "./components/AgentsIndex";
import type { WorkingScopeSearch } from "./lib/workingScope";
import { beginOidcRedirect, isSessionUnauthenticated } from "./lib/auth";
import { readLastActiveScope } from "./lib/preferences";
import { settingsIndexTarget } from "./lib/shellNavigation";
import { AgentGeneralSettings } from "./components/settings/AgentGeneralSettings";
import { TeamAgentsSettings } from "./components/settings/TeamAgentsSettings";
import { SettingsLayout } from "./components/settings/SettingsLayout";
import {
  parseAgentSearch,
  parseAlertsSearch,
  parseApiKeysSearch,
  parseAuditLogSearch,
  parseCheckoutReturnSearch,
  parseCheckoutSearch,
  parseDashboardDetailSearch,
  parseDashboardSearch,
  parseInvitationsSearch,
  parseMarkerEditSearch,
  parseTraceDetailSearch,
  parseMarkersSearch,
  parsePersonasSearch,
  parseScenariosSearch,
  parseScenarioRowsSearch,
  parseScenarioSetsSearch,
  parseSimulationCreateSearch,
  parseSimulationsSearch,
  parseTemplatesSearch,
  parseMembershipsSearch,
  parseTeamsSearch,
  parseTranscriptsSearch,
  parseTranscriptTimelineSearch,
  parseUsageSettingsSearch,
  parseVariablesSearch,
} from "./lib/routerSearch";

// The route search-param schemas live in src/lib/routerSearch.ts so screen
// containers can type-import them without creating a router.tsx import cycle.

// The working scope (docs/09 §4, shell IA v2) rides two optional root-owned
// params — `?team=<uuid>` for team scope and `?agent=<uuid>` for canonical
// agent scope — defined once here, so every child route inherits them and any
// surface can read them via `useWorkingScope`.
export type AppRouteKey =
  | "agent"
  | "agentDetail"
  | "variables"
  | "scenarios"
  | "scenarioRows"
  | "agentScenariosRedirect"
  | "agentScenarioSetsRedirect"
  | "agentsIndex"
  | "transcripts"
  | "transcript"
  | "trace"
  | "personas"
  | "simulations"
  | "simulationCreate"
  | "templateCreate"
  | "templateEdit"
  | "templatesRedirect"
  | "markers"
  | "markerCreate"
  | "markerEdit"
  | "dashboard"
  | "dashboardDetail"
  | "review"
  | "reviewMarker"
  | "alerts"
  | "alertRuleCreate"
  | "alertRuleSilence"
  | "checkout"
  | "checkoutReturn"
  | "settings";

interface AppRouterOptions {
  allowedChildren?: readonly AppRouteKey[];
  guard: {
    authenticatedBeforeLoad: () => void;
    billingComponent?: () => JSX.Element;
    indexBeforeLoad: () => never;
  };
  layout: {
    component: () => JSX.Element;
    notFoundComponent?: () => JSX.Element;
  };
}

export function createAppRouter(options: AppRouterOptions) {
  const rootRoute = createRootRoute({
    component: Outlet,
    notFoundComponent: options.layout.notFoundComponent,
    validateSearch: (search: Record<string, unknown>): WorkingScopeSearch => {
      const next: WorkingScopeSearch = {};
      const agent = search.agent;
      if (typeof agent === "string" && agent.length > 0) {
        next.agent = agent;
      }
      const team = search.team;
      if (typeof team === "string" && team.length > 0) {
        next.team = team;
      }
      return next;
    },
  });

  // The app front door (docs/09 §4): `/` opens the last-active scope's
  // Transcripts. When an agent exists, the URL is canonical agent-only; the
  // parent team stays a display preference and is resolved by the shell.
  const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/",
    beforeLoad: options.guard.indexBeforeLoad,
  });

  // Pathless authenticated layout: every first-ship screen is a child, so the
  // guard runs on entry. Enforcement is cooperative with the client — a 401 /
  // AUTHENTICATION_REQUIRED flips the unauthenticated signal and hands off to the
  // OIDC/SAML redirect; this guard short-circuits client-side navigations once
  // that signal is set. The SPA never refreshes or mints tokens itself.
  const authenticatedRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: "authenticated",
    beforeLoad: options.guard.authenticatedBeforeLoad,
  });

  const billingGateRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    id: "billing-gate",
    component: options.guard.billingComponent ?? Outlet,
  });
  const appShellRoute = createRoute({
    getParentRoute: () => billingGateRoute,
    id: "app-shell",
    component: options.layout.component,
  });
  const checkoutLayoutRoute = createRoute({
    getParentRoute: () => authenticatedRoute,
    id: "checkout-layout",
    component: CheckoutLayout,
  });

  const agentRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/agent",
    component: Agent,
    validateSearch: parseAgentSearch,
  });

  const agentDetailRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/agents/$agentId",
    component: Agent,
    validateSearch: parseAgentSearch,
  });

  const variablesRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/agents/$agentId/variables",
    component: Variables,
    validateSearch: parseVariablesSearch,
  });

  // Scenario authoring is top-level and agent-scoped (founder ratification
  // 2026-07-31), exactly like /personas: `agent_id` stays a query filter,
  // never a path parent. Scenario sets are its second tab (`?tab=sets`).
  const scenariosRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/scenarios",
    component: Scenarios,
    validateSearch: parseScenariosSearch,
  });

  // The spreadsheet-shaped row editor is a scenario child of the top-level
  // Scenarios surface; it deliberately remains outside the shell registry.
  const scenarioRowsRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/scenarios/$scenarioId/rows",
    component: ScenarioRows,
    validateSearch: parseScenarioRowsSearch,
  });

  // The retired agent-nested authoring routes redirect onto the top-level
  // surface carrying the path agent as the root-owned `?agent=` scope, so old
  // deep links (including their drawer subjects) survive.
  const agentScenariosRedirectRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/agents/$agentId/scenarios",
    validateSearch: parseScenariosSearch,
    beforeLoad: ({ params, search }) => {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        // Canonical agent scope is `?agent=` only (docs/09 §4): the path
        // agent becomes the working scope and any stale `?team=` is dropped.
        search: {
          ...search,
          agent: params.agentId,
          tab: undefined,
          team: undefined,
        },
        to: "/scenarios",
      });
    },
  });

  const agentScenarioSetsRedirectRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/agents/$agentId/scenario-sets",
    validateSearch: parseScenarioSetsSearch,
    beforeLoad: ({ params, search }) => {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        search: {
          ...search,
          agent: params.agentId,
          tab: "sets" as const,
          team: undefined,
        },
        to: "/scenarios",
      });
    },
  });

  // The agents index (docs/09 §4): a command-palette/direct-URL destination,
  // not a rail item.
  const agentsIndexRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/agents",
    component: AgentsIndex,
  });

  const transcriptsRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/transcripts",
    component: Transcripts,
    validateSearch: parseTranscriptsSearch,
  });

  const transcriptRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/transcripts/$id",
    component: TranscriptTimeline,
    validateSearch: parseTranscriptTimelineSearch,
  });

  const traceRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/traces/$id",
    component: TraceDetail,
    validateSearch: parseTraceDetailSearch,
  });

  const markersRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/markers",
    component: Markers,
    validateSearch: parseMarkersSearch,
  });

  const personasRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/personas",
    component: Personas,
    validateSearch: parsePersonasSearch,
  });

  // The Simulations launch hub (founder-ratified 2026-07-26, docs/09 §4):
  // queue over /v1/batches; results stay in Transcripts.
  const simulationsRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/simulations",
    component: Simulations,
    validateSearch: parseSimulationsSearch,
  });

  // Authoring-archetype full-spec launch route with the receipt-preview panel.
  const simulationCreateRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/simulations/new",
    component: SimulationCreate,
    validateSearch: parseSimulationCreateSearch,
  });

  // Template authoring is an Authoring route, not a drawer (UI-LAW §4a): the
  // form is multi-section with its own validation lifecycle, and it is the
  // same field UI that authors an ad-hoc launch (docs/14 §5 — a Template IS a
  // saved launch spec, and there is no second launch path).
  const templateCreateRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/simulations/templates/new",
    component: TemplateCreate,
  });

  const templateEditRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/simulations/templates/$templateId",
    component: TemplateEdit,
  });

  // Templates folded into the Simulations hub as a tab (founder ratification
  // 2026-07-31): `/templates` is a redirect. Its legacy drawer params now
  // name authoring ROUTES, so the redirect re-targets them instead of
  // forwarding them as search state.
  const templatesRedirectRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/templates",
    validateSearch: parseTemplatesSearch,
    beforeLoad: ({ search }) => {
      if (search.template !== undefined) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({
          params: { templateId: search.template },
          search: (prev: WorkingScopeSearch) => prev,
          to: "/simulations/templates/$templateId",
        });
      }
      if (search.new === "template") {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({
          search: (prev: WorkingScopeSearch) => prev,
          to: "/simulations/templates/new",
        });
      }
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        search: (prev: WorkingScopeSearch) => ({
          ...prev,
          tab: "templates" as const,
        }),
        to: "/simulations",
      });
    },
  });

  const markerCreateRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/markers/new",
    component: MarkerCreate,
  });

  // Authoring-archetype marker edit route: detail stays a Drawer on /markers,
  // while revise/automate get a shareable route.
  const markerEditRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/markers/$markerId/edit",
    component: MarkerEdit,
    validateSearch: parseMarkerEditSearch,
  });

  const dashboardRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/dashboard",
    component: Dashboard,
    validateSearch: parseDashboardSearch,
  });

  // Dashboard detail renders the selected saved board through the same Board
  // surface as /dashboard.
  const dashboardDetailRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/dashboard/$dashboardId",
    component: Dashboard,
    validateSearch: parseDashboardDetailSearch,
  });

  const reviewRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/review",
    component: ReviewList,
    validateSearch: parseReviewListSearch,
  });

  const reviewMarkerRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/review/$markerId",
    component: ReviewMarker,
    validateSearch: parseReviewMarkerSearch,
  });

  const alertsRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/alerts",
    component: Alerts,
    validateSearch: parseAlertsSearch,
  });

  const alertRuleCreateRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/alerts/rules/new",
    component: AlertRuleCreate,
  });

  const alertRuleSilenceRoute = createRoute({
    getParentRoute: () => appShellRoute,
    path: "/alerts/rules/$ruleId/silence",
    component: AlertRuleSilence,
  });

  // Standalone hosted signup, outside the product shell and billing gate.
  const checkoutRoute = createRoute({
    getParentRoute: () => checkoutLayoutRoute,
    path: "/checkout",
    component: CheckoutScreen,
    validateSearch: parseCheckoutSearch,
  });

  const checkoutReturnRoute = createRoute({
    getParentRoute: () => checkoutLayoutRoute,
    path: "/checkout/return",
    component: CheckoutReturn,
    validateSearch: parseCheckoutReturnSearch,
  });

  // The settings shell (docs/09 §4): a pathless layout route wrapping every
  // /settings/* screen in the SettingsLayout chrome (secondary settings
  // sidebar + the row's screen in the content pane). Child paths are
  // UNCHANGED — the layout contributes chrome, never a URL segment.
  const settingsLayoutRoute = createRoute({
    getParentRoute: () => appShellRoute,
    id: "settings-shell",
    component: SettingsLayout,
  });

  // The /settings index (docs/09 §4): redirects to the agent row when the
  // working scope carries an agent, else the organization row; the root-owned
  // scope params ride the redirect.
  const settingsIndexRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: "/settings",
    beforeLoad: ({ search }) => {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        search: (prev: WorkingScopeSearch) => prev,
        to: settingsIndexTarget(search),
      });
    },
  });

  // /settings/agent — the Agent group's General row, bound to the working
  // `?agent=` scope (docs/09 §4).
  const agentSettingsRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: "/settings/agent",
    component: AgentGeneralSettings,
  });

  const apiKeysRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: "/settings/api-keys",
    component: ApiKeysSettings,
    validateSearch: parseApiKeysSearch,
  });

  const auditLogRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: "/settings/audit-log",
    component: AuditLogSettings,
    validateSearch: parseAuditLogSearch,
  });

  // /settings/billing — hosted-only credit billing (self-gated in the screen).
  const billingSettingsRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: "/settings/billing",
    component: BillingSettings,
  });

  // /settings/phone-numbers — carrier telephony inventory + provisioning.
  // Inventory/caps are universal; carrier actions require provisioning.
  const phoneNumbersSettingsRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: "/settings/phone-numbers",
    component: PhoneNumbersSettings,
  });

  const membershipsRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: "/settings/members",
    component: MembershipsSettings,
    validateSearch: parseMembershipsSearch,
  });

  // /settings/agents — the Team group's archived-agent lifecycle screen.
  const teamAgentsRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: "/settings/agents",
    component: TeamAgentsSettings,
  });

  const invitationsRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: "/settings/invitations",
    component: InvitationsSettings,
    validateSearch: parseInvitationsSearch,
  });

  const organizationSettingsRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: "/settings/organization",
    component: OrganizationSettings,
  });

  // /settings/license — read-only offline-license status
  // (docs/04-api-contract.md, "5a. License status surface";
  // docs/08-on-prem-readiness.md, "XIV — Offline signed licensing"). Always
  // registered; hosted installs render `not_required` cleanly.
  const licenseSettingsRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: "/settings/license",
    component: LicenseSettings,
  });

  const profileSettingsRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: "/settings/profile",
    component: ProfileSettings,
  });

  const retentionRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: "/settings/retention",
    component: RetentionSettings,
  });

  // /settings/usage — the org-wide activity page the sidebar usage row links
  // to (docs/09 §4 Usage row).
  const usageSettingsRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: "/settings/usage",
    component: UsageSettings,
    validateSearch: parseUsageSettingsSearch,
  });

  const teamsSettingsRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: "/settings/teams",
    component: TeamsSettings,
    validateSearch: parseTeamsSearch,
  });

  const teamDetailSettingsRoute = createRoute({
    getParentRoute: () => settingsLayoutRoute,
    path: "/settings/teams/$teamId",
    component: TeamDetailSettings,
  });

  const settingsRoute = settingsLayoutRoute.addChildren([
    settingsIndexRoute,
    agentSettingsRoute,
    apiKeysRoute,
    auditLogRoute,
    billingSettingsRoute,
    phoneNumbersSettingsRoute,
    membershipsRoute,
    invitationsRoute,
    teamAgentsRoute,
    licenseSettingsRoute,
    organizationSettingsRoute,
    profileSettingsRoute,
    retentionRoute,
    usageSettingsRoute,
    teamsSettingsRoute,
    teamDetailSettingsRoute,
  ]);
  const children = [
    ["agent", agentRoute],
    ["agentDetail", agentDetailRoute],
    ["variables", variablesRoute],
    ["scenarios", scenariosRoute],
    ["scenarioRows", scenarioRowsRoute],
    ["agentScenariosRedirect", agentScenariosRedirectRoute],
    ["agentScenarioSetsRedirect", agentScenarioSetsRedirectRoute],
    ["agentsIndex", agentsIndexRoute],
    ["transcripts", transcriptsRoute],
    ["transcript", transcriptRoute],
    ["trace", traceRoute],
    ["personas", personasRoute],
    ["simulations", simulationsRoute],
    ["simulationCreate", simulationCreateRoute],
    ["templateCreate", templateCreateRoute],
    ["templateEdit", templateEditRoute],
    ["templatesRedirect", templatesRedirectRoute],
    ["markers", markersRoute],
    ["markerCreate", markerCreateRoute],
    ["markerEdit", markerEditRoute],
    ["dashboard", dashboardRoute],
    ["dashboardDetail", dashboardDetailRoute],
    ["review", reviewRoute],
    ["reviewMarker", reviewMarkerRoute],
    ["alerts", alertsRoute],
    ["alertRuleCreate", alertRuleCreateRoute],
    ["alertRuleSilence", alertRuleSilenceRoute],
    ["settings", settingsRoute],
  ] as const;
  const allowed = options.allowedChildren;
  const selectedChildren = children
    .filter(([key]) => allowed === undefined || allowed.includes(key))
    .map(([, route]) => route);
  const checkoutChildren = [
    ["checkout", checkoutRoute],
    ["checkoutReturn", checkoutReturnRoute],
  ] as const;
  const selectedCheckout = checkoutChildren
    .filter(([key]) => allowed === undefined || allowed.includes(key))
    .map(([, route]) => route);
  const routeTree = rootRoute.addChildren([
    indexRoute,
    authenticatedRoute.addChildren([
      billingGateRoute.addChildren([
        appShellRoute.addChildren(selectedChildren),
      ]),
      ...(selectedCheckout.length
        ? [checkoutLayoutRoute.addChildren(selectedCheckout)]
        : []),
    ]),
  ]);

  return createRouter({ routeTree });
}

export const router = createAppRouter({
  guard: {
    billingComponent: BillingCheckoutGate,
    authenticatedBeforeLoad: () => {
      if (isSessionUnauthenticated()) {
        beginOidcRedirect();
      }
    },
    indexBeforeLoad: () => {
      const lastActive = readLastActiveScope();
      const search =
        lastActive.agent !== undefined
          ? { agent: lastActive.agent }
          : lastActive.team !== undefined
            ? { team: lastActive.team }
            : {};
      // redirect() returns a framework control-flow signal that must be thrown.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ search, to: "/transcripts" });
    },
  },
  layout: {
    component: AppLayout,
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
